/**
 * useStrategyStats Hook (Web)
 * Thin wrapper around shared useStrategyStats
 *
 * Also re-exports scoring types that were previously co-located here
 * but are now canonical in useParlayScoring.
 */

import { useAuth } from '@contexts/AuthContext';
import { createHttpClient } from '@parlay-picker/shared/api/httpClient';
import {
  type UseStrategyStatsResult,
  useStrategyStats as useStrategyStatsShared,
} from '@parlay-picker/shared/hooks';
import { useMemo } from 'react';
import { API_BASE_URL } from '../config/api';

// Re-export types for existing consumers
// Re-export scoring types that consumers import from this file
export type {
  ParlayScore,
  PlayerHighlight,
  PlayerWarning,
  PredictionStats,
  PropTypeStats,
  ScoringFactor,
  ScoringParlay,
  ScoringResult,
  Stats,
  StrategyPlayerStats as PlayerStats,
  StrategyStats,
  UseStrategyStatsResult,
} from '@parlay-picker/shared/hooks';

/**
 * @param options.enabled When false, skips the auto-fetch. The underlying
 * /api/lab/strategy-stats + /api/lab/prediction-stats endpoints are admin-only
 * (403 for everyone else), so non-admin callers must pass enabled:false to
 * avoid 403-spamming on every page load.
 */
export function useStrategyStats(options: { enabled?: boolean } = {}): UseStrategyStatsResult {
  const { enabled = true } = options;
  const { authFetch } = useAuth();

  const httpClient = useMemo(
    () => createHttpClient({ authFetch, baseUrl: API_BASE_URL }),
    [authFetch],
  );

  return useStrategyStatsShared({ httpClient, autoLoad: enabled });
}
